import React, { useState, useEffect } from 'react';
import { 
  User, Briefcase, Star, Clock, Award, Globe, 
  Save, CheckCircle, XCircle, Loader2, DollarSign 
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { authApi, settingsApi } from '../services';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ExpertProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    is_expert: false,
    specializations: [],
    bio: '',
    years_experience: 0,
    hourly_rate: null,
    hourly_rates: {
      standard: 1500,
      complex: 2000,
      urgent: 2500,
      consultation: 1200
    },
    availability: 'AVAILABLE',
    certifications: [],
    languages: ['NO'],
    preferred_case_types: [],
    org_nr: '',
    address: '',
    bank_account: ''
  });
  const [availableSpecializations, setAvailableSpecializations] = useState([]);

  useEffect(() => {
    loadProfile();
    loadSpecializations();
  }, []);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/experts/profile/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProfile(response.data.expert_profile || profile);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Kunne ikke laste profil');
      setLoading(false);
    }
  };

  const loadSpecializations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/experts/specializations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAvailableSpecializations(response.data.specializations || []);
    } catch (error) {
      console.error('Failed to load specializations:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/experts/profile`,
        profile,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success('Ekspertprofil oppdatert!');
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error('Kunne ikke lagre profil');
    } finally {
      setSaving(false);
    }
  };

  const toggleSpecialization = (spec) => {
    if (profile.specializations.includes(spec)) {
      setProfile({
        ...profile,
        specializations: profile.specializations.filter(s => s !== spec)
      });
    } else {
      setProfile({
        ...profile,
        specializations: [...profile.specializations, spec]
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Ekspertprofil
          </h1>
          <p className="text-slate-600">
            Bli en del av Lexy Network og tilby din ekspertise til andre advokater
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Expert Status Toggle */}
          <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-6 rounded-full relative transition-all ${
                  profile.is_expert ? 'bg-blue-600' : 'bg-slate-300'
                }`}>
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                    profile.is_expert ? 'left-7' : 'left-1'
                  }`} />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Aktiv som ekspert</p>
                  <p className="text-sm text-slate-600">
                    {profile.is_expert 
                      ? 'Du er synlig i ekspertnetverket'
                      : 'Aktiver for å motta forespørsler'}
                  </p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={profile.is_expert}
                onChange={(e) => setProfile({...profile, is_expert: e.target.checked})}
                className="hidden"
              />
            </label>
          </div>

          {/* Bio Section */}
          <div className="mb-8">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
              <User className="w-4 h-4" />
              Profesjonell bio
            </label>
            <textarea
              value={profile.bio}
              onChange={(e) => setProfile({...profile, bio: e.target.value})}
              placeholder="Fortell om din bakgrunn og erfaring..."
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={4}
            />
          </div>

          {/* Experience & Availability */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
                <Briefcase className="w-4 h-4" />
                År erfaring
              </label>
              <input
                type="number"
                value={profile.years_experience}
                onChange={(e) => setProfile({...profile, years_experience: parseInt(e.target.value) || 0})}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                min="0"
              />
            </div>
            
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
                <Clock className="w-4 h-4" />
                Tilgjengelighet
              </label>
              <select
                value={profile.availability}
                onChange={(e) => setProfile({...profile, availability: e.target.value})}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="AVAILABLE">Tilgjengelig</option>
                <option value="BUSY">Opptatt</option>
                <option value="UNAVAILABLE">Ikke tilgjengelig</option>
              </select>
            </div>
          </div>

          {/* Hourly Rates */}
          <div className="mb-8">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
              <Star className="w-4 h-4" />
              Timepriser (NOK)
            </label>
            <p className="text-sm text-slate-600 mb-4">
              Sett ulike priser for forskjellige typer oppdrag
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Standard</label>
                <input
                  type="number"
                  value={profile.hourly_rates?.standard || 1500}
                  onChange={(e) => setProfile({
                    ...profile, 
                    hourly_rates: {...profile.hourly_rates, standard: parseInt(e.target.value) || 1500}
                  })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Komplekse saker</label>
                <input
                  type="number"
                  value={profile.hourly_rates?.complex || 2000}
                  onChange={(e) => setProfile({
                    ...profile, 
                    hourly_rates: {...profile.hourly_rates, complex: parseInt(e.target.value) || 2000}
                  })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Haster/Urgent</label>
                <input
                  type="number"
                  value={profile.hourly_rates?.urgent || 2500}
                  onChange={(e) => setProfile({
                    ...profile, 
                    hourly_rates: {...profile.hourly_rates, urgent: parseInt(e.target.value) || 2500}
                  })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Konsultasjon</label>
                <input
                  type="number"
                  value={profile.hourly_rates?.consultation || 1200}
                  onChange={(e) => setProfile({
                    ...profile, 
                    hourly_rates: {...profile.hourly_rates, consultation: parseInt(e.target.value) || 1200}
                  })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Banking Info for Invoicing */}
          <div className="mb-8">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
              <Star className="w-4 h-4" />
              Fakturainfo
            </label>
            <p className="text-sm text-slate-600 mb-4">
              Nødvendig for å sende fakturaer
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Org. nummer (valgfri)</label>
                <input
                  type="text"
                  value={profile.org_nr || ''}
                  onChange={(e) => setProfile({...profile, org_nr: e.target.value})}
                  placeholder="123456789"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Kontonummer</label>
                <input
                  type="text"
                  value={profile.bank_account || ''}
                  onChange={(e) => setProfile({...profile, bank_account: e.target.value})}
                  placeholder="1234 56 78901"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-slate-600 mb-1 block">Adresse</label>
                <input
                  type="text"
                  value={profile.address || ''}
                  onChange={(e) => setProfile({...profile, address: e.target.value})}
                  placeholder="Gate 123, 0123 Oslo"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Specializations */}
          <div className="mb-8">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
              <Award className="w-4 h-4" />
              Spesialiseringer
            </label>
            <p className="text-sm text-slate-600 mb-4">
              Velg områder du er ekspert på
            </p>
            <div className="flex flex-wrap gap-2">
              {availableSpecializations.map(spec => (
                <button
                  key={spec}
                  onClick={() => toggleSpecialization(spec)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    profile.specializations.includes(spec)
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {spec}
                </button>
              ))}
            </div>
          </div>

          {/* Languages */}
          <div className="mb-8">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
              <Globe className="w-4 h-4" />
              Språk
            </label>
            <div className="flex flex-wrap gap-2">
              {['NO', 'EN', 'SV', 'DA'].map(lang => (
                <button
                  key={lang}
                  onClick={() => {
                    if (profile.languages.includes(lang)) {
                      setProfile({
                        ...profile,
                        languages: profile.languages.filter(l => l !== lang)
                      });
                    } else {
                      setProfile({
                        ...profile,
                        languages: [...profile.languages, lang]
                      });
                    }
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    profile.languages.includes(lang)
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-6 border-t">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Lagrer...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Lagre profil
                </>
              )}
            </button>
          </div>
        </div>

        {/* Info Cards */}
        {profile.is_expert && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border border-green-100">
              <CheckCircle className="w-8 h-8 text-green-600 mb-3" />
              <h3 className="font-semibold text-slate-900 mb-2">Du er nå synlig!</h3>
              <p className="text-sm text-slate-600">
                Andre advokater kan finne deg og invitere deg til samarbeid
              </p>
            </div>
            
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-100">
              <Star className="w-8 h-8 text-blue-600 mb-3" />
              <h3 className="font-semibold text-slate-900 mb-2">Bygg omdømme</h3>
              <p className="text-sm text-slate-600">
                Få vurderinger og bygg tillit i nettverket
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpertProfile;
